import { useCallback, useEffect, useMemo, useState } from "react";

export const CURRENCY_PREFERENCE_STORAGE_KEY = "sparelynk-currency-preference";
export const CURRENCY_PREFERENCE_UPDATED_EVENT =
  "sparelynk:currency-preference-updated";
export const SHIPPING_FEE_SOURCE_CURRENCY = "USD";

export type CurrencyPreference = {
  nationality: string;
  preferredCurrency: string;
};

export const NATIONALITY_OPTIONS = [
  ["Argentina", "ARS"],
  ["Australia", "AUD"],
  ["Austria", "EUR"],
  ["Bahrain", "BHD"],
  ["Bangladesh", "BDT"],
  ["Belgium", "EUR"],
  ["Brazil", "BRL"],
  ["Canada", "CAD"],
  ["Chile", "CLP"],
  ["China", "CNY"],
  ["Colombia", "COP"],
  ["Czech Republic", "CZK"],
  ["Denmark", "DKK"],
  ["Egypt", "EGP"],
  ["Finland", "EUR"],
  ["France", "EUR"],
  ["Germany", "EUR"],
  ["Ghana", "GHS"],
  ["Greece", "EUR"],
  ["Hong Kong", "HKD"],
  ["Hungary", "HUF"],
  ["India", "INR"],
  ["Indonesia", "IDR"],
  ["Ireland", "EUR"],
  ["Israel", "ILS"],
  ["Italy", "EUR"],
  ["Japan", "JPY"],
  ["Kenya", "KES"],
  ["Kuwait", "KWD"],
  ["Malaysia", "MYR"],
  ["Mexico", "MXN"],
  ["Nepal", "NPR"],
  ["Netherlands", "EUR"],
  ["New Zealand", "NZD"],
  ["Nigeria", "NGN"],
  ["Norway", "NOK"],
  ["Oman", "OMR"],
  ["Pakistan", "PKR"],
  ["Peru", "PEN"],
  ["Philippines", "PHP"],
  ["Poland", "PLN"],
  ["Portugal", "EUR"],
  ["Qatar", "QAR"],
  ["Romania", "RON"],
  ["Saudi Arabia", "SAR"],
  ["Singapore", "SGD"],
  ["South Africa", "ZAR"],
  ["South Korea", "KRW"],
  ["Spain", "EUR"],
  ["Sri Lanka", "LKR"],
  ["Sweden", "SEK"],
  ["Switzerland", "CHF"],
  ["Thailand", "THB"],
  ["Turkey", "TRY"],
  ["United Arab Emirates", "AED"],
  ["United Kingdom", "GBP"],
  ["United States", "USD"],
  ["Vietnam", "VND"],
] as const;

export const CURRENCY_OPTIONS = [
  "AED",
  "ARS",
  "AUD",
  "BDT",
  "BHD",
  "BRL",
  "CAD",
  "CHF",
  "CLP",
  "CNY",
  "COP",
  "CZK",
  "DKK",
  "EGP",
  "EUR",
  "GBP",
  "GHS",
  "HKD",
  "HUF",
  "IDR",
  "ILS",
  "INR",
  "JPY",
  "KES",
  "KRW",
  "KWD",
  "LKR",
  "MXN",
  "MYR",
  "NGN",
  "NOK",
  "NPR",
  "NZD",
  "OMR",
  "PEN",
  "PHP",
  "PKR",
  "PLN",
  "QAR",
  "RON",
  "SAR",
  "SEK",
  "SGD",
  "THB",
  "TRY",
  "USD",
  "VND",
  "ZAR",
] as const;

const CURRENCY_LOCALES: Record<string, string> = {
  AED: "ar-AE",
  ARS: "es-AR",
  AUD: "en-AU",
  BDT: "bn-BD",
  BHD: "ar-BH",
  BRL: "pt-BR",
  CAD: "en-CA",
  CHF: "de-CH",
  CLP: "es-CL",
  CNY: "zh-CN",
  COP: "es-CO",
  CZK: "cs-CZ",
  DKK: "da-DK",
  EGP: "ar-EG",
  EUR: "en-IE",
  GBP: "en-GB",
  GHS: "en-GH",
  HKD: "zh-HK",
  HUF: "hu-HU",
  IDR: "id-ID",
  ILS: "he-IL",
  INR: "en-IN",
  JPY: "ja-JP",
  KES: "en-KE",
  KRW: "ko-KR",
  KWD: "ar-KW",
  LKR: "en-LK",
  MXN: "es-MX",
  MYR: "ms-MY",
  NGN: "en-NG",
  NOK: "nb-NO",
  NPR: "ne-NP",
  NZD: "en-NZ",
  OMR: "ar-OM",
  PEN: "es-PE",
  PHP: "en-PH",
  PKR: "en-PK",
  PLN: "pl-PL",
  QAR: "ar-QA",
  RON: "ro-RO",
  SAR: "ar-SA",
  SEK: "sv-SE",
  SGD: "en-SG",
  THB: "th-TH",
  TRY: "tr-TR",
  USD: "en-US",
  VND: "vi-VN",
  ZAR: "en-ZA",
};

const nationalityCurrencyMap = new Map<string, string>(
  NATIONALITY_OPTIONS.map(
    ([nationality, currency]) =>
      [nationality.toLowerCase(), currency] as const,
  ),
);

function normalizeCurrency(value: unknown): string {
  const currency = String(value ?? "")
    .trim()
    .toUpperCase();
  return /^[A-Z]{3}$/.test(currency) ? currency : "";
}

export function normalizeCurrencyCode(value: unknown, fallback = ""): string {
  return normalizeCurrency(value) || normalizeCurrency(fallback);
}

export function resolveProductCurrency(
  product: {
    currency?: unknown;
    specifications?: Record<string, string> | null;
  } | null | undefined,
  fallback = "USD",
): string {
  return (
    normalizeCurrency(product?.currency) ||
    normalizeCurrency(product?.specifications?.Currency) ||
    normalizeCurrency(product?.specifications?.currency) ||
    normalizeCurrency(fallback) ||
    "USD"
  );
}

export function resolveShippingFeeCurrency(
  product: {
    baseShippingFeeCurrency?: unknown;
    shippingFeeCurrency?: unknown;
    shippingCurrency?: unknown;
  } | null | undefined,
  fallback = SHIPPING_FEE_SOURCE_CURRENCY,
): string {
  return (
    normalizeCurrency(product?.baseShippingFeeCurrency) ||
    normalizeCurrency(product?.shippingFeeCurrency) ||
    normalizeCurrency(product?.shippingCurrency) ||
    normalizeCurrency(fallback) ||
    SHIPPING_FEE_SOURCE_CURRENCY
  );
}

export function currencyForNationality(nationality: string): string {
  return nationalityCurrencyMap.get(nationality.trim().toLowerCase()) ?? "USD";
}

export function currencyLocale(currency: string): string {
  return CURRENCY_LOCALES[normalizeCurrency(currency)] ?? "en-US";
}

export function currencySymbol(currency: unknown): string {
  const safeCurrency = normalizeCurrency(currency) || "USD";
  try {
    const part = new Intl.NumberFormat(currencyLocale(safeCurrency), {
      style: "currency",
      currency: safeCurrency,
      currencyDisplay: "narrowSymbol",
    })
      .formatToParts(0)
      .find((entry) => entry.type === "currency");
    return part?.value || safeCurrency;
  } catch {
    return safeCurrency;
  }
}

export function readCurrencyPreference(): CurrencyPreference {
  if (typeof window === "undefined") {
    return { nationality: "", preferredCurrency: "USD" };
  }

  try {
    const parsed = JSON.parse(
      window.localStorage.getItem(CURRENCY_PREFERENCE_STORAGE_KEY) ?? "{}",
    ) as Partial<CurrencyPreference>;
    const nationality = String(parsed.nationality ?? "").trim();
    const preferredCurrency =
      normalizeCurrency(parsed.preferredCurrency) ||
      (nationality ? currencyForNationality(nationality) : "USD");
    return { nationality, preferredCurrency };
  } catch {
    return { nationality: "", preferredCurrency: "USD" };
  }
}

export function writeCurrencyPreference(
  preference: Partial<CurrencyPreference>,
): CurrencyPreference {
  const nationality = String(preference.nationality ?? "").trim();
  const preferredCurrency =
    normalizeCurrency(preference.preferredCurrency) ||
    (nationality ? currencyForNationality(nationality) : "USD");
  const normalized = { nationality, preferredCurrency };

  if (typeof window !== "undefined") {
    window.localStorage.setItem(
      CURRENCY_PREFERENCE_STORAGE_KEY,
      JSON.stringify(normalized),
    );
    window.dispatchEvent(
      new CustomEvent<CurrencyPreference>(CURRENCY_PREFERENCE_UPDATED_EVENT, {
        detail: normalized,
      }),
    );
  }

  return normalized;
}

export function preferredCurrency(fallback = "USD"): string {
  return (
    readCurrencyPreference().preferredCurrency ||
    normalizeCurrency(fallback) ||
    "USD"
  );
}

export function usePreferredCurrency(fallback = "USD"): string {
  const [currency, setCurrency] = useState(() => preferredCurrency(fallback));

  useEffect(() => {
    const syncPreference = () => {
      setCurrency(preferredCurrency(fallback));
    };
    const handlePreferenceUpdate = (event: Event) => {
      const preferenceEvent = event as CustomEvent<CurrencyPreference>;
      setCurrency(
        normalizeCurrency(preferenceEvent.detail?.preferredCurrency) ||
          preferredCurrency(fallback),
      );
    };
    const handleStorage = (event: StorageEvent) => {
      if (!event.key || event.key === CURRENCY_PREFERENCE_STORAGE_KEY) {
        syncPreference();
      }
    };

    syncPreference();
    window.addEventListener(
      CURRENCY_PREFERENCE_UPDATED_EVENT,
      handlePreferenceUpdate,
    );
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(
        CURRENCY_PREFERENCE_UPDATED_EVENT,
        handlePreferenceUpdate,
      );
      window.removeEventListener("storage", handleStorage);
    };
  }, [fallback]);

  return currency;
}


const EXCHANGE_RATE_CACHE_KEY = "sparelynk-exchange-rates-usd-v1";
const EXCHANGE_RATE_UPDATED_EVENT = "sparelynk:exchange-rates-updated";
const EXCHANGE_RATE_BASE = "USD";
const EXCHANGE_RATE_MAX_AGE_MS = 12 * 60 * 60 * 1000;
const EXCHANGE_RATE_REQUEST_TIMEOUT_MS = 6000;
const EXCHANGE_RATE_ENDPOINTS = [
  "https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/usd.min.json",
  "https://latest.currency-api.pages.dev/v1/currencies/usd.min.json",
  "https://open.er-api.com/v6/latest/USD",
] as const;

type ExchangeRateCache = {
  base: string;
  date?: string;
  fetchedAt: number;
  rates: Record<string, number>;
};

export type CurrencyConversionResult = {
  amount: number;
  currency: string;
  converted: boolean;
};

let exchangeRates: Record<string, number> = { USD: 1 };
let exchangeRatesFetchedAt = 0;
let exchangeRatesHydrated = false;
let exchangeRatesRequest: Promise<Record<string, number>> | null = null;

function normalizeAmount(value: unknown): number {
  const amount = Number(value ?? 0);
  return Number.isFinite(amount) ? amount : 0;
}

function normalizeRates(value: unknown): Record<string, number> {
  if (!value || typeof value !== "object") return {};

  const root = value as Record<string, unknown>;
  const candidates = [root.usd, root.USD, root.rates];
  const source = candidates.find(
    (candidate) => candidate && typeof candidate === "object",
  ) as Record<string, unknown> | undefined;
  if (!source) return {};

  const normalized: Record<string, number> = { USD: 1 };
  Object.entries(source).forEach(([currency, rawRate]) => {
    const code = normalizeCurrency(currency);
    const rate = Number(rawRate);
    if (code && Number.isFinite(rate) && rate > 0) {
      normalized[code] = rate;
    }
  });
  return normalized;
}

function hydrateExchangeRates(): void {
  if (exchangeRatesHydrated || typeof window === "undefined") return;
  exchangeRatesHydrated = true;

  try {
    const parsed = JSON.parse(
      window.localStorage.getItem(EXCHANGE_RATE_CACHE_KEY) ?? "{}",
    ) as Partial<ExchangeRateCache>;
    const rates = normalizeRates({ rates: parsed.rates });
    if (Object.keys(rates).length > 1) {
      exchangeRates = rates;
      exchangeRatesFetchedAt = Number(parsed.fetchedAt ?? 0);
    }
  } catch {
    exchangeRates = { USD: 1 };
    exchangeRatesFetchedAt = 0;
  }
}

function publishExchangeRates(cache: ExchangeRateCache): void {
  exchangeRates = cache.rates;
  exchangeRatesFetchedAt = cache.fetchedAt;

  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(EXCHANGE_RATE_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Conversion still works for the current page when storage is unavailable.
  }
  window.dispatchEvent(
    new CustomEvent<ExchangeRateCache>(EXCHANGE_RATE_UPDATED_EVENT, {
      detail: cache,
    }),
  );
}

async function fetchExchangeRates(): Promise<ExchangeRateCache> {
  let lastError: unknown;

  for (const endpoint of EXCHANGE_RATE_ENDPOINTS) {
    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort(),
      EXCHANGE_RATE_REQUEST_TIMEOUT_MS,
    );

    try {
      const response = await fetch(endpoint, {
        cache: "no-store",
        headers: { Accept: "application/json" },
        signal: controller.signal,
      });
      if (!response.ok) {
        throw new Error(`Exchange-rate request failed with ${response.status}`);
      }

      const payload = (await response.json()) as Record<string, unknown>;
      const rates = normalizeRates(payload);
      if (Object.keys(rates).length <= 1) {
        throw new Error("Exchange-rate response did not contain usable rates");
      }

      return {
        base: EXCHANGE_RATE_BASE,
        date:
          String(payload.date ?? payload.time_last_update_utc ?? "").trim() ||
          undefined,
        fetchedAt: Date.now(),
        rates,
      };
    } catch (error) {
      lastError = error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  throw lastError instanceof Error
    ? lastError
    : new Error("Unable to load exchange rates");
}

export async function loadExchangeRates(force = false): Promise<Record<string, number>> {
  hydrateExchangeRates();
  const cacheIsFresh =
    exchangeRatesFetchedAt > 0 &&
    Date.now() - exchangeRatesFetchedAt < EXCHANGE_RATE_MAX_AGE_MS;

  if (!force && cacheIsFresh && Object.keys(exchangeRates).length > 1) {
    return exchangeRates;
  }
  if (exchangeRatesRequest) return exchangeRatesRequest;

  exchangeRatesRequest = fetchExchangeRates()
    .then((cache) => {
      publishExchangeRates(cache);
      return cache.rates;
    })
    .catch(() => exchangeRates)
    .finally(() => {
      exchangeRatesRequest = null;
    });

  return exchangeRatesRequest;
}

export function canConvertCurrency(
  sourceCurrency: unknown,
  targetCurrency: unknown,
): boolean {
  hydrateExchangeRates();
  const source = normalizeCurrency(sourceCurrency) || EXCHANGE_RATE_BASE;
  const target = normalizeCurrency(targetCurrency) || EXCHANGE_RATE_BASE;
  return (
    source === target ||
    (Number.isFinite(exchangeRates[source]) &&
      exchangeRates[source] > 0 &&
      Number.isFinite(exchangeRates[target]) &&
      exchangeRates[target] > 0)
  );
}

export function convertCurrencyAmount(
  value: unknown,
  sourceCurrency: unknown,
  targetCurrency: unknown,
): number | null {
  hydrateExchangeRates();
  const amount = normalizeAmount(value);
  const source = normalizeCurrency(sourceCurrency) || EXCHANGE_RATE_BASE;
  const target = normalizeCurrency(targetCurrency) || EXCHANGE_RATE_BASE;
  if (source === target) return amount;

  const sourceRate = exchangeRates[source];
  const targetRate = exchangeRates[target];
  if (
    !Number.isFinite(sourceRate) ||
    sourceRate <= 0 ||
    !Number.isFinite(targetRate) ||
    targetRate <= 0
  ) {
    return null;
  }

  return (amount / sourceRate) * targetRate;
}

export function currencyConversion(
  value: unknown,
  sourceCurrency: unknown,
  targetCurrency: unknown = preferredCurrency(),
): CurrencyConversionResult {
  const amount = normalizeAmount(value);
  const source =
    normalizeCurrency(sourceCurrency) || normalizeCurrency(targetCurrency) || "USD";
  const target = normalizeCurrency(targetCurrency) || source;
  const convertedAmount = convertCurrencyAmount(amount, source, target);

  if (convertedAmount === null) {
    void loadExchangeRates();
    return { amount, currency: source, converted: false };
  }

  return {
    amount: convertedAmount,
    currency: target,
    converted: source !== target,
  };
}

export function formatCurrencyCodeAmount(
  value: unknown,
  currency: unknown,
): string {
  const amount = normalizeAmount(value);
  const safeCurrency = normalizeCurrency(currency) || preferredCurrency();
  const formattedAmount = Math.abs(amount).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${safeCurrency} ${amount < 0 ? "-" : ""}${formattedAmount}`;
}

export function currencyCodeNumberFormat(currency: unknown): string {
  const safeCurrency = normalizeCurrency(currency) || preferredCurrency();
  const escapedCurrency = safeCurrency.replace(/"/g, '""');
  return `"${escapedCurrency} "#,##0.00;[Red]"${escapedCurrency} "-#,##0.00`;
}

export function formatCurrencyAmount(
  value: unknown,
  currency: unknown,
  locale?: string,
): string {
  const amount = normalizeAmount(value);
  const safeCurrency = normalizeCurrency(currency) || "USD";
  const safeLocale = locale?.trim() || currencyLocale(safeCurrency);

  try {
    return new Intl.NumberFormat(safeLocale, {
      style: "currency",
      currency: safeCurrency,
    }).format(amount);
  } catch {
    return `${safeCurrency} ${amount.toFixed(2)}`;
  }
}

export function formatPreferredCurrency(
  value: unknown,
  sourceCurrency: unknown = "USD",
): string {
  const result = currencyConversion(
    value,
    sourceCurrency,
    preferredCurrency(),
  );
  return formatCurrencyAmount(result.amount, result.currency);
}

export function useCurrencyConverter(fallback = "USD") {
  const preferred = usePreferredCurrency(fallback);
  const [rateRevision, setRateRevision] = useState(0);

  useEffect(() => {
    const refreshRates = () => setRateRevision((revision) => revision + 1);
    const handleStorage = (event: StorageEvent) => {
      if (event.key === EXCHANGE_RATE_CACHE_KEY) {
        exchangeRatesHydrated = false;
        hydrateExchangeRates();
        refreshRates();
      }
    };

    hydrateExchangeRates();
    void loadExchangeRates();
    window.addEventListener(EXCHANGE_RATE_UPDATED_EVENT, refreshRates);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener(EXCHANGE_RATE_UPDATED_EVENT, refreshRates);
      window.removeEventListener("storage", handleStorage);
    };
  }, [preferred]);

  const convert = useCallback(
    (
      value: unknown,
      sourceCurrency: unknown = fallback,
      targetCurrency: unknown = preferred,
    ) => currencyConversion(value, sourceCurrency, targetCurrency),
    [fallback, preferred, rateRevision],
  );

  const format = useCallback(
    (value: unknown, sourceCurrency: unknown = fallback) => {
      const result = currencyConversion(value, sourceCurrency, preferred);
      return formatCurrencyAmount(result.amount, result.currency);
    },
    [fallback, preferred, rateRevision],
  );

  const inputCurrency = useCallback(
    (sourceCurrency: unknown = fallback) => {
      const source = normalizeCurrency(sourceCurrency) || "USD";
      return canConvertCurrency(source, preferred) ? preferred : source;
    },
    [fallback, preferred, rateRevision],
  );

  const convertInputToSource = useCallback(
    (value: unknown, sourceCurrency: unknown = fallback): number | null => {
      const source = normalizeCurrency(sourceCurrency) || "USD";
      const enteredCurrency = canConvertCurrency(source, preferred)
        ? preferred
        : source;
      return convertCurrencyAmount(value, enteredCurrency, source);
    },
    [fallback, preferred, rateRevision],
  );

  return useMemo(
    () => ({
      preferredCurrency: preferred,
      convert,
      format,
      inputCurrency,
      convertInputToSource,
      rateRevision,
    }),
    [convert, convertInputToSource, format, inputCurrency, preferred, rateRevision],
  );
}
